import { Play, Check, Pause, X, RotateCcw, Loader2 } from 'lucide-react'
import { useChangeTaskStatus } from '../hooks'
import type { Task, TaskStatus } from '../types'

interface Props {
  task: Task
}

type Action = {
  /** Tekst etykiety na przycisku. */
  label: string
  /** Ikona z lucide-react. */
  icon: React.ComponentType<{ className?: string }>
  /** Docelowy status taska po kliknięciu. */
  status: TaskStatus
  /** Styling - 'primary' = sky, 'success' = green, 'ghost' = slate neutral. */
  variant: 'primary' | 'success' | 'ghost'
}

/** Mapuje aktualny status taska na zestaw kontekstowych akcji.
 *  Kolejność wpływa na render (pierwsza = leftmost). */
function actionsFor(status: TaskStatus): Action[] {
  switch (status) {
    case 'NEW':
      return [
        { label: 'Rozpocznij', icon: Play, status: 'IN_PROGRESS', variant: 'primary' },
        { label: 'Anuluj', icon: X, status: 'CANCELLED', variant: 'ghost' },
      ]
    case 'IN_PROGRESS':
      return [
        { label: 'Zakończ', icon: Check, status: 'DONE', variant: 'success' },
        { label: 'Wstrzymaj', icon: Pause, status: 'NEW', variant: 'ghost' },
        { label: 'Anuluj', icon: X, status: 'CANCELLED', variant: 'ghost' },
      ]
    case 'DONE':
      return [
        { label: 'Otwórz ponownie', icon: RotateCcw, status: 'IN_PROGRESS', variant: 'ghost' },
      ]
    case 'CANCELLED':
      return [
        { label: 'Otwórz ponownie', icon: RotateCcw, status: 'NEW', variant: 'ghost' },
      ]
  }
}

const VARIANT_CLASSES: Record<Action['variant'], string> = {
  primary: 'bg-sky-600 text-white hover:bg-sky-700',
  success: 'bg-emerald-600 text-white hover:bg-emerald-700',
  ghost: 'border border-slate-300 text-slate-700 hover:bg-slate-100',
}

/** Pasek kontekstowych akcji zmiany statusu pokazywany w TaskDetailDrawer.
 *  Eliminuje konieczność przeciągania karty na kanbanie żeby zaktualizować status. */
export function TaskStatusActions({ task }: Props) {
  const changeStatus = useChangeTaskStatus()
  const actions = actionsFor(task.status)

  return (
    <div className="flex flex-wrap gap-2">
      {actions.map((a) => {
        const Icon = a.icon
        const busy = changeStatus.isPending && changeStatus.variables?.status === a.status
        return (
          <button
            key={a.status}
            type="button"
            onClick={() => changeStatus.mutate({ id: task.id, status: a.status })}
            disabled={changeStatus.isPending}
            className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition disabled:opacity-50 ${VARIANT_CLASSES[a.variant]}`}
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icon className="h-3.5 w-3.5" />}
            {a.label}
          </button>
        )
      })}
    </div>
  )
}
